/**
 * Tiny fetch wrapper. Always sends cookies (credentials: 'include') so the
 * Lucia session cookie travels on every call, including the dev :5173 -> :3000
 * proxy.
 */

export type ApiError = { error: string; [k: string]: unknown };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
    ...init,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = (data || { error: 'request_failed' }) as ApiError;
    err.error = err.error || `http_${res.status}`;
    throw err;
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export type Me = {
  user: {
    id: string;
    email: string;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    role: 'student' | 'instructor' | 'admin';
    status: 'pending' | 'active' | 'paused' | 'completed' | 'refunded';
    enrolledAt: string | null;
  } | null;
};

export type PublicConfig = {
  supportEmail: string;
};

export type LessonInModule = {
  slug: string;
  title: string;
  orderInModule: number;
  completed: boolean;
  inProgress: boolean;
  unlocked: boolean;
};

export type ModuleSummary = {
  id: string;
  slug: string;
  weekNumber: number;
  title: string;
  summary: string | null;
  lessonCount: number;
  completedCount: number;
  inProgressLessonSlug: string | null;
  unlocked: boolean;
  unlockAt: string | null;
  lessons?: LessonInModule[];
};
